import * as React from "react";
import { useRef, useMemo, useState, useEffect } from "react";
import Select from "@/components/Select";
import IconTrash from "@/icons/IconTrash";
import VariablePicker from "@/components/VariablePicker";
import {
  FormValidator,
  QuestionType,
  questionTypeDefs,
  SurveyQuestion
} from "@velaro/velaro-component-lib";
import useSubscriptions from "@/hooks/useSubscriptions";
import { languageOptionsList } from "@/constants/languages";
import ReactSelect from "react-select";
import useApi from "@/hooks/useApi";

// Extended question type with fields beyond the shared component-lib type
export interface VisibilityCondition {
  questionIndex: number;
  operator:
    | "equals"
    | "not_equals"
    | "contains"
    | "starts_with"
    | "is_empty"
    | "is_not_empty";
  value: string;
}

export interface HiddenFieldConfig {
  source: "pageUrl" | "jsVariable" | "staticValue" | "embedParam";
  jsPath?: string;
  staticValue?: string;
}

export interface GroupConfig {
  excludedGroupIds?: number[];
  customLabels?: Record<string, string>;
}

export interface ExtSurveyQuestion extends SurveyQuestion {
  satisfactionMetricType?: string;
  visibilityCondition?: VisibilityCondition | null;
  hiddenConfig?: HiddenFieldConfig | null;
  placeholderText?: string;
  displayMode?: "dropdown" | "radio";
  groupConfig?: GroupConfig | null;
}

const defaultLabelForType: Record<string, string> = {
  name: "What is your name?",
  email: "What is your email address?",
  text: "",
  textArea: "",
  choice: "",
  checkbox: "",
  star: "How would you rate us?",
  netPromoterScore: "How likely are you to recommend us? (0-10)",
  crmField: "",
  language: "What is your preferred language?",
  label: "",
  select: "",
  multiSelect: "",
  pageBreak: "",
  visitorPhone: "What is your phone number?",
  tags: "Select tags for this conversation",
  hidden: "",
  group: "Which team can we connect you with?",
  groupRadio: "Which team can we connect you with?"
};

const TYPE_BADGE: Record<
  string,
  { label: string; colors: string }
> = {
  name: { label: "Name", colors: "bg-blue-100 text-blue-700" },
  email: { label: "Email", colors: "bg-blue-100 text-blue-700" },
  text: { label: "Text", colors: "bg-slate-100 text-slate-600" },
  textArea: { label: "Long text", colors: "bg-slate-100 text-slate-600" },
  choice: { label: "Dropdown", colors: "bg-violet-100 text-violet-700" },
  checkbox: { label: "Checkbox", colors: "bg-purple-100 text-purple-700" },
  star: { label: "Star rating", colors: "bg-amber-100 text-amber-700" },
  netPromoterScore: { label: "NPS", colors: "bg-orange-100 text-orange-700" },
  crmField: { label: "CRM field", colors: "bg-teal-100 text-teal-700" },
  language: { label: "Language", colors: "bg-emerald-100 text-emerald-700" },
  label: { label: "Label / divider", colors: "bg-slate-100 text-slate-500" },
  select: { label: "Select", colors: "bg-violet-100 text-violet-700" },
  multiSelect: { label: "Multi-select", colors: "bg-purple-100 text-purple-700" },
  pageBreak: { label: "Page break", colors: "bg-slate-100 text-slate-400" },
  visitorPhone: { label: "Phone", colors: "bg-sky-100 text-sky-700" },
  tags: { label: "Tags", colors: "bg-rose-100 text-rose-700" },
  hidden: { label: "Hidden", colors: "bg-amber-100 text-amber-600" },
  group: { label: "Group dropdown", colors: "bg-indigo-100 text-indigo-700" },
  groupRadio: { label: "Group radio", colors: "bg-indigo-100 text-indigo-700" }
};

const OPERATOR_OPTIONS = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "doesn't equal" },
  { value: "contains", label: "contains" },
  { value: "starts_with", label: "starts with" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is answered" }
];

function hasFixedVariable(type: QuestionType | string) {
  switch (type) {
    case "name":
    case "email":
    case "crmField":
    case "language":
    case "visitorPhone":
    case "group":
    case "groupRadio":
      return true;
    default:
      return false;
  }
}

function canTrackSatisfaction(type: string) {
  return type === "star" || type === "netPromoterScore";
}

interface Props {
  question: ExtSurveyQuestion;
  errors: Record<string, string>;
  index: number;
  allQuestions: ExtSurveyQuestion[];
  netSuiteMappings?: any[];
  loadingMappings?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  update(question: ExtSurveyQuestion): void;
  remove(): void;
  moveUp?(): void;
  moveDown?(): void;
}

export default function QuestionEditor({
  question,
  errors,
  index,
  allQuestions,
  netSuiteMappings = [],
  loadingMappings = false,
  isFirst = false,
  isLast = false,
  dragHandleProps,
  update,
  remove,
  moveUp,
  moveDown
}: Props) {
  const variableFixed = hasFixedVariable(question.type);
  const { subscription } = useSubscriptions();
  const api = useApi();
  const labelInputRef = useRef<HTMLInputElement>(null);
  const [showSkipLogic, setShowSkipLogic] = useState(
    !!question.visibilityCondition
  );
  const [availableGroups, setAvailableGroups] = useState<{ id: number; name: string }[]>([]);

  // Sync state when the question prop changes (e.g. a different question lands at this index slot)
  useEffect(() => {
    setShowSkipLogic(!!question.visibilityCondition);
  }, [question.visibilityCondition]);

  const isGroupType = (question.type as string) === "group" || (question.type as string) === "groupRadio";

  useEffect(() => {
    if (!isGroupType || availableGroups.length > 0) return;
    api.messaging.get("Messenger/Survey/groups").then(async (res) => {
      if (res.ok) {
        const data = await res.json();
        setAvailableGroups(Array.isArray(data) ? data : []);
      }
    }).catch(() => {});
  }, [isGroupType, api.messaging, availableGroups.length]);

  const availableQuestionTypes = useMemo(() => {
    const allOptions = Object.entries(questionTypeDefs).map(
      ([value, label]) => ({ value, label })
    );
    const filteredOptions = !subscription?.enableNetsuite
      ? allOptions.filter((option) => option.value !== "crmField")
      : allOptions;
    const extras: { value: string; label: string }[] = [
      { value: "pageBreak", label: "Page break" },
      { value: "visitorPhone", label: "Phone number" },
      { value: "tags", label: "Tags (agent surveys only)" },
      { value: "hidden", label: "Hidden field" },
      { value: "group", label: "Group (dropdown)" },
      { value: "groupRadio", label: "Group (radio buttons)" }
    ];
    // Keep deprecated types selectable if this question already uses them
    if ((question.type as string) === "select")
      extras.push({ value: "select", label: "Select (deprecated — use Dropdown)" });
    if ((question.type as string) === "multiSelect")
      extras.push({ value: "multiSelect", label: "Multi-select (deprecated — use Checkbox)" });
    return [...filteredOptions, ...extras];
  }, [subscription?.enableNetsuite, question.type]);

  const netSuiteOptions = useMemo(() => {
    if (loadingMappings) return [{ label: "Loading...", value: "" }];
    if (!netSuiteMappings || netSuiteMappings.length === 0)
      return [{ label: "No NetSuite fields available", value: "" }];
    return netSuiteMappings.map((mapping) => ({
      label: `${mapping.recordType} - ${mapping.netSuiteField}`,
      value: mapping.netSuiteField,
      recordType: mapping.recordType,
      netSuiteField: mapping.netSuiteField
    }));
  }, [netSuiteMappings, loadingMappings]);

  const optionsList = useMemo(
    () => (question.options ? question.options.join("\n") : ""),
    [question.options]
  );

  const typeHasOptions =
    question.type === "choice" ||
    question.type === "checkbox" ||
    question.type === "crmField" ||
    (question.type as string) === "language" ||
    (question.type as string) === "select" ||
    (question.type as string) === "multiSelect" ||
    (question.type as string) === "tags";

  const typeHasInput =
    question.type !== "label" &&
    (question.type as string) !== "pageBreak" &&
    (question.type as string) !== "hidden";

  // Questions before this one that have a variable (can be watched)
  const watchableQuestions = allQuestions
    .slice(0, index)
    .map((q, qi) => ({ q, qi }))
    .filter(({ q }) => q.type !== "label" && (q.type as string) !== "pageBreak" && (q.type as string) !== "hidden");

  function changeType(type: QuestionType | string) {
    const wasFixed = variableFixed;
    const isFixed = hasFixedVariable(type);
    const questionType = type as QuestionType;
    const autoMetric: string | undefined =
      type === "star" ? "star_rating" : type === "netPromoterScore" ? "nps" : undefined;

    const oldDefault = defaultLabelForType[question.type] ?? "";
    const newLabel =
      !question.text || question.text === oldDefault
        ? (defaultLabelForType[type] ?? "")
        : question.text;

    const base = { ...question, type: questionType, text: newLabel, satisfactionMetricType: autoMetric };

    if (type === "language") {
      update({ ...base, variable: "language", options: [] });
    } else if (type === "visitorPhone") {
      update({ ...base, variable: "phone" });
    } else if (type === "group" || type === "groupRadio") {
      update({ ...base, variable: "group", options: [] });
    } else if (type === "pageBreak" || type === "hidden") {
      update({ ...base, variable: undefined, required: false, options: [], satisfactionMetricType: undefined });
    } else if (isFixed) {
      update({ ...base, variable: type });
    } else if (wasFixed) {
      update({ ...base, variable: "" });
    } else {
      update(base);
    }
  }

  function insertVariableIntoLabel(variable: string) {
    const input = labelInputRef.current;
    if (!input) {
      update({ ...question, text: question.text + variable });
      return;
    }
    const start = input.selectionStart ?? question.text.length;
    const end = input.selectionEnd ?? question.text.length;
    const newText =
      question.text.slice(0, start) + variable + question.text.slice(end);
    update({ ...question, text: newText });
    requestAnimationFrame(() => {
      input.setSelectionRange(
        start + variable.length,
        start + variable.length
      );
      input.focus();
    });
  }

  const cond = question.visibilityCondition;

  function toggleSkipLogic() {
    if (showSkipLogic) {
      update({ ...question, visibilityCondition: null });
      setShowSkipLogic(false);
    } else {
      const firstWatchable = watchableQuestions[0];
      update({
        ...question,
        visibilityCondition: {
          questionIndex: firstWatchable?.qi ?? 0,
          operator: "equals",
          value: ""
        }
      });
      setShowSkipLogic(true);
    }
  }

  function updateCondition(patch: Partial<VisibilityCondition>) {
    update({
      ...question,
      visibilityCondition: { ...cond!, ...patch }
    });
  }

  const badge = TYPE_BADGE[question.type] ?? {
    label: question.type,
    colors: "bg-slate-100 text-slate-500"
  };
  const variableDisplay =
    (variableFixed ? question.variable : question.variable) || null;

  const isPageBreak = (question.type as string) === "pageBreak";
  const isHidden = (question.type as string) === "hidden";

  return (
    <div className={`border rounded-xl shadow-sm hover:shadow-md transition-shadow duration-150 ${isHidden ? "border-dashed border-amber-300 bg-amber-50" : "bg-white border-slate-200"}`}>
      {/* ── Header row: reorder + type + trash ── */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        {/* Drag handle (DnD mode) or move up/down arrows (fallback) */}
        {dragHandleProps ? (
          <div
            {...dragHandleProps}
            aria-label="Drag to reorder"
            className="shrink-0 flex items-center justify-center w-6 h-8 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing transition-colors"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M7 4a1 1 0 100 2 1 1 0 000-2zm6 0a1 1 0 100 2 1 1 0 000-2zM7 9a1 1 0 100 2 1 1 0 000-2zm6 0a1 1 0 100 2 1 1 0 000-2zm-6 5a1 1 0 100 2 1 1 0 000-2zm6 0a1 1 0 100 2 1 1 0 000-2z" />
            </svg>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5 shrink-0">
            <button
              type="button"
              onClick={moveUp}
              disabled={isFirst}
              aria-label="Move question up"
              className="w-5 h-5 flex items-center justify-center rounded text-slate-300 hover:text-blue-500 hover:bg-blue-50 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l5 5a1 1 0 01-1.414 1.414L10 5.414 5.707 9.707a1 1 0 01-1.414-1.414l5-5A1 1 0 0110 3z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              type="button"
              onClick={moveDown}
              disabled={isLast}
              aria-label="Move question down"
              className="w-5 h-5 flex items-center justify-center rounded text-slate-300 hover:text-blue-500 hover:bg-blue-50 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                <path fillRule="evenodd" d="M10 17a1 1 0 01-.707-.293l-5-5a1 1 0 011.414-1.414L10 14.586l4.293-4.293a1 1 0 011.414 1.414l-5 5A1 1 0 0110 17z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {/* Question number */}
        <span className="text-xs font-semibold text-slate-300 w-5 shrink-0 text-center">
          {index + 1}
        </span>

        {/* Type selector */}
        <div className="shrink-0">
          <Select
            options={availableQuestionTypes}
            className="input text-sm py-1"
            value={question.type || "text"}
            onChange={changeType}
          />
        </div>

        {/* Type badge */}
        <span
          className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 ${badge.colors}`}
        >
          {badge.label}
        </span>

        <div className="flex-1" />

        {/* Trash */}
        <button
          type="button"
          onClick={remove}
          aria-label="Delete question"
          className="p-1.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
        >
          <IconTrash />
        </button>
      </div>

      {/* ── Body ── */}
      <div className="px-4 pb-4 space-y-3">
        {/* Hidden field banner */}
        {isHidden && (
          <div className="flex items-center gap-2 text-xs text-amber-700 font-medium">
            <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            Hidden — not visible to visitor
          </div>
        )}

        {/* Page break visual indicator */}
        {isPageBreak && (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <div className="flex-1 border-t-2 border-dashed border-slate-300" />
            <span className="shrink-0 px-2">page break</span>
            <div className="flex-1 border-t-2 border-dashed border-slate-300" />
          </div>
        )}

        {/* Tags warning — not rendered in visitor-facing surveys */}
        {(question.type as string) === "tags" && (
          <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-700">
            <svg className="w-3.5 h-3.5 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span><strong>Agent surveys only.</strong> Tags are not rendered in visitor-facing pre/post-chat surveys. Only use this field type in internal agent surveys.</span>
          </div>
        )}

        {/* Question label + variable picker (not for pageBreak or hidden) */}
        {!isPageBreak && !isHidden && (
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Question label
            </label>
            <FormValidator error={errors["text"]} errorClassName="absolute">
              <div className="flex items-center gap-2">
                <input
                  ref={labelInputRef}
                  type="text"
                  className="input flex-1 min-w-0"
                  required
                  placeholder="Enter question text..."
                  value={question.text}
                  onChange={(e) =>
                    update({ ...question, text: e.currentTarget.value })
                  }
                />
                <VariablePicker onInsert={insertVariableIntoLabel} />
              </div>
            </FormValidator>
          </div>
        )}

        {/* Hidden field config editor */}
        {isHidden && (
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Variable name
              </label>
              <input
                type="text"
                className="input w-full"
                placeholder="variable_name"
                value={question.variable || ""}
                onChange={(e) =>
                  update({ ...question, variable: e.currentTarget.value })
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Value source
              </label>
              <select
                className="input w-full"
                aria-label="Hidden field value source"
                value={(question as ExtSurveyQuestion).hiddenConfig?.source ?? "pageUrl"}
                onChange={(e) => {
                  const source = e.currentTarget.value as HiddenFieldConfig["source"];
                  update({
                    ...question,
                    hiddenConfig: { ...((question as ExtSurveyQuestion).hiddenConfig ?? {}), source }
                  } as ExtSurveyQuestion);
                }}
              >
                <option value="pageUrl">Page URL</option>
                <option value="jsVariable">JavaScript variable</option>
                <option value="staticValue">Static value</option>
                <option value="embedParam">Embed parameter</option>
              </select>
            </div>
            {(question as ExtSurveyQuestion).hiddenConfig?.source === "jsVariable" && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  JavaScript path
                </label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="e.g. window.myData.userId"
                  value={(question as ExtSurveyQuestion).hiddenConfig?.jsPath ?? ""}
                  onChange={(e) =>
                    update({
                      ...question,
                      hiddenConfig: {
                        ...((question as ExtSurveyQuestion).hiddenConfig ?? { source: "jsVariable" }),
                        jsPath: e.currentTarget.value
                      }
                    } as ExtSurveyQuestion)
                  }
                />
              </div>
            )}
            {(question as ExtSurveyQuestion).hiddenConfig?.source === "staticValue" && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Static value
                </label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="e.g. campaign-2026"
                  value={(question as ExtSurveyQuestion).hiddenConfig?.staticValue ?? ""}
                  onChange={(e) =>
                    update({
                      ...question,
                      hiddenConfig: {
                        ...((question as ExtSurveyQuestion).hiddenConfig ?? { source: "staticValue" }),
                        staticValue: e.currentTarget.value
                      }
                    } as ExtSurveyQuestion)
                  }
                />
              </div>
            )}
          </div>
        )}

        {/* Variable + Required row */}
        {typeHasInput && (
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Variable name
              </label>
              <input
                disabled={variableFixed}
                type="text"
                className={`input w-full ${variableFixed ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""}`}
                placeholder="variable_name"
                value={variableDisplay || question.variable || ""}
                onChange={(e) =>
                  update({ ...question, variable: e.currentTarget.value })
                }
              />
              {(variableDisplay || question.variable) && (
                <div className="mt-1 inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-600 font-mono text-[11px] px-2 py-0.5 rounded-full">
                  <span className="opacity-60">{"<<"}</span>
                  <span>{variableDisplay || question.variable}</span>
                  <span className="opacity-60">{">>"}</span>
                </div>
              )}
              {variableFixed && (
                <div className="mt-1 text-[11px] text-slate-400">
                  Fixed for this question type
                </div>
              )}
            </div>

            {/* Required toggle */}
            <div className="shrink-0 pt-5">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div
                  onClick={() =>
                    update({ ...question, required: !question.required })
                  }
                  role="checkbox"
                  aria-checked={question.required}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter")
                      update({ ...question, required: !question.required });
                  }}
                  className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                    question.required ? "bg-blue-500" : "bg-slate-200"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      question.required ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </div>
                <span className="text-xs font-medium text-slate-600">Required</span>
              </label>
            </div>

            {/* Satisfaction metric — auto-assigned, no dropdown needed */}
            {canTrackSatisfaction(question.type) && (
              <div className="shrink-0 pt-5">
                <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-medium px-2 py-1 rounded-full">
                  Auto-tracked as {question.type === "star" ? "Star rating (CSAT)" : "NPS (0–10)"}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Options section */}
        {typeHasOptions && (
          <div>
            {question.type === "crmField" ? (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  NetSuite field
                </label>
                <Select
                  options={netSuiteOptions}
                  className="input w-full"
                  value={question.variable || ""}
                  onChange={(value) => {
                    const selectedOption = netSuiteOptions.find(
                      (opt) => opt.value === value
                    );
                    if (
                      selectedOption &&
                      "recordType" in selectedOption &&
                      selectedOption.recordType
                    ) {
                      update({
                        ...question,
                        variable: value,
                        crmOptions: {
                          recordType: selectedOption.recordType,
                          netSuiteField: selectedOption.netSuiteField
                        }
                      });
                    } else {
                      update({
                        ...question,
                        variable: value,
                        crmOptions: undefined
                      });
                    }
                  }}
                />
              </div>
            ) : (question.type as string) === "language" ? (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Limit to languages (blank = all)
                </label>
                <ReactSelect
                  isMulti
                  name="languages"
                  options={languageOptionsList.map((lang) => ({
                    value: lang,
                    label: lang
                  }))}
                  value={(question.options || [])
                    .filter((o) => o.trim())
                    .map((lang) => ({ value: lang, label: lang }))}
                  className="basic-multi-select w-full"
                  classNamePrefix="select"
                  placeholder="Select languages (blank = all)..."
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({ ...base, minHeight: "40px" }),
                    valueContainer: (base) => ({
                      ...base,
                      flexWrap: "wrap",
                      maxHeight: "120px",
                      overflowY: "auto"
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                  onChange={(selected) => {
                    const val = selected ? selected.map((item) => item.value) : [];
                    update({ ...question, options: val });
                  }}
                />
                <div className="mt-1 text-xs text-slate-400">
                  {question.options && question.options.length > 0
                    ? `${question.options.filter((o) => o.trim()).length} language(s) selected`
                    : `Blank = all ${languageOptionsList.length} languages`}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Options{" "}
                  <span className="font-normal text-slate-400">(one per line)</span>
                </label>
                <textarea
                  className="input w-full resize-none"
                  rows={4}
                  placeholder={"Option 1\nOption 2\nOption 3"}
                  value={optionsList}
                  onChange={(e) => {
                    const val = e.currentTarget.value.split("\n");
                    update({ ...question, options: val });
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Display mode toggle — choice type only */}
        {(question.type as string) === "choice" && (
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Display as
            </label>
            <div className="flex gap-3">
              {(["dropdown", "radio"] as const).map((mode) => (
                <label key={mode} className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="radio"
                    name={`displayMode-${question.id ?? index}`}
                    value={mode}
                    checked={((question as ExtSurveyQuestion).displayMode ?? "dropdown") === mode}
                    onChange={() => update({ ...question, displayMode: mode } as ExtSurveyQuestion)}
                    className="accent-cornflower-blue-500"
                  />
                  <span className="text-xs text-slate-700 capitalize">{mode === "dropdown" ? "Dropdown" : "Radio buttons"}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Placeholder text — for text-entry types */}
        {(question.type === "text" || question.type === "textArea" || question.type === "name" || question.type === "email" || (question.type as string) === "visitorPhone") && (
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Placeholder text <span className="font-normal text-slate-400">(shown inside the field before visitor types)</span>
            </label>
            <input
              type="text"
              className="input w-full"
              placeholder={`e.g. "Your answer here..."`}
              value={(question as ExtSurveyQuestion).placeholderText ?? ""}
              onChange={(e) =>
                update({ ...question, placeholderText: e.currentTarget.value || undefined } as ExtSurveyQuestion)
              }
            />
          </div>
        )}

        {/* Group config — for group/groupRadio types */}
        {isGroupType && (
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Exclude teams <span className="font-normal text-slate-400">(hidden from visitor — optional)</span>
            </label>
            {availableGroups.length === 0 ? (
              <div className="text-xs text-slate-400 italic">Loading teams...</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableGroups.map((g) => {
                  const excluded = ((question as ExtSurveyQuestion).groupConfig?.excludedGroupIds ?? []).includes(g.id);
                  return (
                    <label key={g.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs cursor-pointer select-none transition-colors ${
                      excluded ? "border-red-300 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}>
                      <input
                        type="checkbox"
                        className="w-3 h-3"
                        checked={excluded}
                        onChange={() => {
                          const cfg = (question as ExtSurveyQuestion).groupConfig ?? {};
                          const ids = cfg.excludedGroupIds ?? [];
                          const next = excluded ? ids.filter((id) => id !== g.id) : [...ids, g.id];
                          update({ ...question, groupConfig: { ...cfg, excludedGroupIds: next } } as ExtSurveyQuestion);
                        }}
                      />
                      {g.name}
                    </label>
                  );
                })}
              </div>
            )}
            <p className="mt-1.5 text-[11px] text-slate-400">
              The visitor&apos;s answer is stored as <code className="font-mono bg-slate-100 px-1 rounded">{"<<group>>"}</code> and can be used in routing rules to direct the conversation.
            </p>
          </div>
        )}

        {/* ── Skip logic ── */}
        <div className="border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={toggleSkipLogic}
            disabled={watchableQuestions.length === 0 && !showSkipLogic}
            className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
              watchableQuestions.length === 0 && !showSkipLogic
                ? "text-slate-300 cursor-not-allowed"
                : showSkipLogic
                  ? "text-red-500 hover:text-red-600"
                  : "text-blue-500 hover:text-blue-600"
            }`}
            title={
              watchableQuestions.length === 0 && !showSkipLogic
                ? allQuestions.slice(0, index).length === 0
                  ? "Add at least one question before this one to enable conditional display"
                  : "The questions before this one are label/divider types — add a question with an answer field (text, name, dropdown, etc.) before this one"
                : undefined
            }
          >
            {showSkipLogic ? (
              <>
                <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Remove condition
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Show only when...
              </>
            )}
          </button>

          {watchableQuestions.length === 0 && !showSkipLogic && (
            <p className="mt-1 text-[11px] text-slate-400">
              Add a question above this one to enable conditional display.
            </p>
          )}

          {showSkipLogic && cond && (
            <div className="mt-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="text-[11px] font-semibold text-blue-600 uppercase tracking-wide mb-2">
                Conditional display
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500 font-medium">Show when</span>

                {/* Which question to watch */}
                <select
                  className="input text-sm py-1 pr-6"
                  value={cond.questionIndex}
                  onChange={(e) =>
                    updateCondition({ questionIndex: Number(e.currentTarget.value) })
                  }
                >
                  {watchableQuestions.map(({ q, qi }) => (
                    <option key={qi} value={qi}>
                      Q{qi + 1}: {q.text ? q.text.slice(0, 40) : `(untitled)`}
                      {q.variable ? ` — <<${q.variable}>>` : ""}
                    </option>
                  ))}
                </select>

                {/* Operator */}
                <select
                  className="input text-sm py-1 pr-6"
                  value={cond.operator}
                  onChange={(e) =>
                    updateCondition({
                      operator: e.currentTarget.value as VisibilityCondition["operator"]
                    })
                  }
                >
                  {OPERATOR_OPTIONS.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>

                {/* Value (hidden for is_empty / is_not_empty) */}
                {cond.operator !== "is_empty" &&
                  cond.operator !== "is_not_empty" && (
                    <input
                      type="text"
                      className="input text-sm py-1 w-32"
                      placeholder="value..."
                      value={cond.value}
                      onChange={(e) =>
                        updateCondition({ value: e.currentTarget.value })
                      }
                    />
                  )}
              </div>
              <p className="mt-2 text-[11px] text-blue-500 leading-relaxed">
                This question will only appear when the condition above is true.
                Hidden questions are skipped automatically — their answers won&apos;t be
                submitted.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
